import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../../types-enums/activity";
import {isActivityCarBusState, isActivityFlightState, isActivityHotelState} from "../../../utils/check/type-guards";
import FlightRoute from "../../../molecules/activity/string-display/flight-route/FlightRoute";
import {MAX_WIDTH_PERCENTAGE_DND_ITEM} from "../journey-overview/journey-day-card-items/JourneyDayCardItems";
import HotelLocation from "../../../molecules/activity/string-display/hotel-location/HotelLocation";
import React from "react";
import {ColorKeys} from "atmosfair-ui";
import cloneDeep from "lodash/cloneDeep";
import {setCo2KgEToOneDayEquivalent} from "../../../utils/setter/activities";
import ActivityEmissionDisplay
    from "../../../molecules/activity/string-display/activity-emission-display/ActivityEmissionDisplay";
import CarBusInformation from "../../../molecules/activity/string-display/car-bus-information/CarBusInformation";
import {getActivityPersonCount} from "../../../utils/getter/activities";
import {WhereToDisplayActivityString} from "../../../utils/string/common";

export const renderActivityForJourney = (
    activity: AllActivityTypes,
    activities: AllActivityTypesArray,
    journeyHasExactDates: boolean,
    textColor: ColorKeys,
    loading: boolean,
    iconColor: string,
    suppressIconHints: boolean,
    totalCo2KgEIncludingReturnEmissionValues: boolean,
) => {
    const {common: {activityType}} = activity
    let ActivityStringComponent
    const personCount = getActivityPersonCount(activity)
    const EmissionsCo2KgEComponent =
        <ActivityEmissionDisplay
            totalCo2KgEIncludingReturnEmissionValues={totalCo2KgEIncludingReturnEmissionValues}
            allActivities={activities}
            textColor={textColor}
            activity={activity}/>
    const commonProps = {
        iconColor,
        journeyWithExactDates: journeyHasExactDates,
        whereToDisplay: WhereToDisplayActivityString.journey,
        truncatedWordsListMaxWidthPercentage: MAX_WIDTH_PERCENTAGE_DND_ITEM,
        textColor: textColor,
    }
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            if (isActivityFlightState(activity)) {
                ActivityStringComponent =
                    <FlightRoute
                        showReturnIcon={false}
                        loading={loading}
                        flight={activity}
                        {...commonProps}/>
            }
            break;
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(activity)) {
                ActivityStringComponent = <HotelLocation
                    suppressExactDatesHint={suppressIconHints}
                    hotel={activity}
                    {...commonProps}/>
            }
            break;
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(activity)) {
                ActivityStringComponent = <CarBusInformation
                    hideDifferentDurationHint={suppressIconHints}
                    hideDifferentRouteHint={suppressIconHints}
                    carBus={activity}
                    {...commonProps}
                />
            }
            break;
        default:
            return activityType satisfies never
    }
    return {personCount, ActivityStringComponent, EmissionsCo2KgEComponent}
}

/**
 * Returns the adjusted CO2 KGE value of an Activity. This is needed if i.e. a hotel has a duration of three days,
 * then the calculated CO2 KGE of this hotel is for three days. However, if the hotel is moved to one day (i.e.
 * in the modal from which the user can create a journey from travel components), the CO2 KGE emissions must
 * be adjusted to reflect only one day. This is only true for Activities that can have a duration span (like hotels).
 */
export const updateCo2OfActivityBasedOnDuration = (activity: AllActivityTypes): AllActivityTypes => {
    const updatedActivity = cloneDeep(activity)
    const {common: {activityType}} = updatedActivity
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            return updatedActivity
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(updatedActivity)) setCo2KgEToOneDayEquivalent(updatedActivity, true)
            return updatedActivity
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(updatedActivity)) setCo2KgEToOneDayEquivalent(updatedActivity, true)
            return updatedActivity
        default:
            activityType satisfies never;
    }
    return updatedActivity
}